import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

type Options = {
	duration?: number;
};

export const usePanelTransition = ({ duration = 0.65 }: Options = {}) => {
	const startPanelRef = useRef<HTMLDivElement | null>(null);
	const gamePanelRef = useRef<HTMLDivElement | null>(null);
	const [isStartActive, setIsStartActive] = useState(true);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const timelineRef = useRef<gsap.core.Timeline | null>(null);

	const initializePanels = useCallback(() => {
		if (startPanelRef.current) {
			startPanelRef.current.style.pointerEvents = 'auto';
			gsap.set(startPanelRef.current, { x: '0%' });
		}
		if (gamePanelRef.current) {
			gamePanelRef.current.style.pointerEvents = 'none';
			gsap.set(gamePanelRef.current, { x: '-100%' });
		}
	}, []);

	useLayoutEffect(() => {
		initializePanels();
	}, [initializePanels]);

	useEffect(() => {
		return () => {
			timelineRef.current?.kill();
		};
	}, []);

	const transitionToGame = useCallback(() => {
		if (isTransitioning || !isStartActive) {
			return;
		}

		setIsTransitioning(true);
		timelineRef.current?.kill();

		if (!startPanelRef.current || !gamePanelRef.current) {
			setIsStartActive(false);
			setIsTransitioning(false);
			return;
		}

		timelineRef.current = gsap
			.timeline({
				defaults: { duration, ease: 'power2.inOut' },
				onComplete: () => {
					setIsStartActive(false);
					setIsTransitioning(false);
				},
			})
			.to(
				startPanelRef.current,
				{
					x: '100%',
					pointerEvents: 'none',
				},
				0,
			)
			.to(
				gamePanelRef.current,
				{
					x: '0%',
					pointerEvents: 'auto',
				},
				'<',
			);
	}, [duration, isStartActive, isTransitioning]);

	const shouldRenderStartPanel = isStartActive || isTransitioning;

	const resetPanels = useCallback(() => {
		timelineRef.current?.kill();
		initializePanels();
		setIsStartActive(true);
		setIsTransitioning(false);
	}, [initializePanels]);

	return {
		startPanelRef,
		gamePanelRef,
		isTransitioning,
		transitionToGame,
		shouldRenderStartPanel,
		resetPanels,
	};
};

export type UsePanelTransitionReturn = ReturnType<typeof usePanelTransition>;

export default usePanelTransition;
